import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Slider, Card, List, Avatar, Upload, Image, Button, message, Input } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import './css/CatFoodDetail.css';
import catFaceFull from '../components/img/cat-face-full.png';
import catFaceHalf from '../components/img/cat-face-half.png';
import catFaceEmpty from '../components/img/cat-face-empty.png';
import NotificationHub from '../components/NotificationHub';
import { API_BASE_URL } from '../config/api';
import { get, post, isAuthenticated } from '../utils/api';

const { TextArea } = Input;

const BACKEND_URL = API_BASE_URL;

const RatingIcons = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="rating-icons">
      {[...Array(fullStars)].map((_, i) => (
        <img key={`full-${i}`} src={catFaceFull} alt="Full Star" className="rating-icon" />
      ))}
      {hasHalfStar && <img src={catFaceHalf} alt="Half Star" className="rating-icon" />}
      {[...Array(emptyStars)].map((_, i) => (
        <img key={`empty-${i}`} src={catFaceEmpty} alt="Empty Star" className="rating-icon" />
      ))}
    </div>
  );
};

const CatFoodDetail = () => {
  const { id } = useParams();
  const [food, setFood] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRating, setUserRating] = useState(0);
  const [userComment, setUserComment] = useState('');
  const [foodComments, setFoodComments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [fileList, setFileList] = useState([]);

  const isUserLoggedIn = isAuthenticated();

  const addNotification = useCallback((msgText, type = 'info') => {
    const id = Date.now();
    setNotifications(prevNotifications => [...prevNotifications, { id, msgText, type }]);
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications(prevNotifications => prevNotifications.filter(notification => notification.id !== id));
  }, []);

  useEffect(() => {
    const fetchFoodDetail = async () => {
      try {
        const data = await get(`/api/cat-foods/${id}/`);
        setFood(data);
        setUserRating(data.customer_rate); // Set slider initial value to product's current rating
        setLoading(false);
      } catch (error) {
        console.error('Error fetching cat food details:', error);
        setError('Failed to fetch cat food details. Please try again later.');
        setLoading(false);
      }
    };

    const fetchFoodComments = async () => {
      try {
        const data = await get(`/api/cat-foods/${id}/comments/`);
        setFoodComments(data);
      } catch (error) {
        console.error('Error fetching food comments:', error);
      }
    };

    fetchFoodDetail();
    fetchFoodComments();
  }, [id]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    return imagePath.startsWith('http') ? imagePath : `${BACKEND_URL}${imagePath}`;
  };

  const handleRatingChange = (value) => {
    setUserRating(value);
  };

  const handleCommentChange = (e) => {
    setUserComment(e.target.value);
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过5MB!');
    }
    return isImage && isLt5M;
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!userComment.trim() && fileList.length === 0) {
      message.error('请输入评论内容或上传图片');
      return;
    }

    try {
      // Create FormData to handle file uploads
      const formData = new FormData();
      formData.append('food_id', id);
      formData.append('rating', userRating);
      formData.append('comment', userComment);
      
      // Append each file to the FormData
      fileList.forEach((file) => {
        formData.append('uploaded_images', file.originFileObj);
      });
      
      // Use fetch directly for FormData
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/cat-foods/comments/create/`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Refresh comments to get updated data with images
      const updatedComments = await get(`/api/cat-foods/${id}/comments/`);
      setFoodComments(updatedComments);
      
      setUserRating(0);
      setUserComment('');
      setFileList([]);
      
      addNotification('评论提交成功', 'success');
    } catch (error) {
      console.error('Error submitting review:', error);
      addNotification('评论提交失败，请稍后重试', 'error');
    }
  };

  if (loading) return <div className="loading">加载猫粮详情中...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!food) return <div className="error">未找到猫粮。</div>;

  return (
    <div className="cat-food-detail">
      <div className="food-detail-content">
        <div className="food-info">
          <div className="food-image">
            {food.image && <img src={getImageUrl(food.image)} alt={food.name} className="food-image-large" />}
          </div>
          <div className="food-details">
            <h4>{food.name}</h4>
            <RatingIcons rating={food.customer_rate} />
            <p><strong>价格:</strong> ¥{food.price}</p>
            <p><strong>重量:</strong> {food.weight}kg</p>
            <p><strong>品牌:</strong> {food.brand}</p>
            <p><strong>成分:</strong> {food.ingredients}</p>
            <p><strong>添加剂:</strong> {food.additive}</p>
            <p><strong>保质期:</strong> {food.expiration}</p>
            <p><strong>种类:</strong> {food.variation}</p>
          </div>
        </div>
        <Card title="用户评价" className="food-comments-card">
          {foodComments.length > 0 ? (
            <List
              itemLayout="horizontal"
              dataSource={foodComments}
              renderItem={comment => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Link to={`/user-profile/${comment.user?.id}`}>
                        <Avatar src={comment.user?.profile_picture_url || `https://xsgames.co/randomusers/avatar.php?g=pixel&key=${comment.user?.id}`} />
                      </Link>
                    }
                    title={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Link to={`/user-profile/${comment.user?.id}`}>{comment.user ? comment.user.username : '匿名用户'}</Link>
                        <div style={{ marginLeft: '10px' }}>
                          <RatingIcons rating={comment.rating} />
                        </div>
                      </div>
                    }
                    description={
                      <div>
                        <div>{comment.comment}</div>
                        {comment.images && comment.images.length > 0 && (
                          <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {comment.images.map((img, index) => (
                              <Image
                                key={index}
                                src={img.image_url || `${BACKEND_URL}${img.image}`}
                                alt={`Comment image ${index + 1}`}
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                preview={{ src: img.image_url || `${BACKEND_URL}${img.image}` }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    }
                  />
                  <div>{new Date(comment.created_at).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                </List.Item>
              )}
            />
          ) : (
            <p>暂无评价</p>
          )}
        </Card>

        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>添加评价</span>
              <Upload
                listType="text"
                fileList={[]}
                onChange={handleFileChange}
                beforeUpload={beforeUpload}
                multiple={true}
                showUploadList={false}
                customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
              >
                <Button 
                  icon={<PictureOutlined />} 
                  style={{ width: '120px', height: '32px' }}
                >
                  添加图片
                </Button>
              </Upload>
            </div>
          } 
          className="new-comment-card"
        >
          <form onSubmit={handleSubmitReview} className="review-form">
            <div className="review-header">
              <div className="rating-input">
                <label htmlFor="rating">评分:</label>
                <Slider
                  min={0}
                  max={5}
                  step={0.5}
                  value={userRating}
                  onChange={handleRatingChange}
                  style={{ width: 200 }}
                />
                <span className="current-rate">{userRating}</span>
              </div>
            </div>
            
            {/* Display uploaded images above the text box */}
            {fileList.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {fileList.map((file, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img 
                        src={file.url || URL.createObjectURL(file.originFileObj)} 
                        alt={`Preview ${index}`} 
                        style={{ width: '100px', height: '100px', objectFit: 'cover' }} 
                      />
                      <div 
                        style={{ 
                          position: 'absolute', 
                          top: 5, 
                          right: 5,
                          background: 'rgba(230, 155, 155, 0.4)',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontSize: '12px',
                          lineHeight: 1,
                          cursor: 'pointer',
                          color: '#ff4d4f'
                        }}
                        onClick={() => {
                          const newFileList = [...fileList];
                          newFileList.splice(index, 1);
                          setFileList(newFileList);
                        }}
                      >
                        X
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="comment-input">
              <TextArea
                rows={4}
                value={userComment}
                onChange={handleCommentChange}
                placeholder="输入您的评价..."
                style={{ marginBottom: '10px' }}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button 
                type="primary" 
                htmlType="submit"
                className="submit-review"
              >
                提交评价
              </Button>
            </div>
          </form>
        </Card>
      </div>
      <NotificationHub
        notifications={notifications}
        onNotificationDismiss={dismissNotification}
      />
    </div>
  );
};

export default CatFoodDetail;
